// saml-client/client/saml-federation-login.ts
import { A } from "@adornis/base/env-info.js";
import "@adornis/chemistry/elements/components/x-card.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import { XDialog } from "@adornis/dialog/x-dialog.js";
import { useEffect, useObservable, useState } from "@adornis/functional-lit/stateful.js";
import { useQuery } from "@adornis/functional-lit/use-query.js";
import { webcomponent } from "@adornis/functional-lit/webcomponent.js";
import { clientTranslator } from "@adornis/translation-core/client/clientTranslator.js";
import { globalLanguageSubject } from "@adornis/translation-core/client/languageSubject.js";
import { html } from "lit";
import { popupAuthFlow } from "../../pop-up-login-flow/authPopup.js";
import { getIdPList } from "../api/resolver.js";
import { getSamlLoginRoute, loginWithSAML } from "./loginWithSAML.js";
var SamlFederationLogin = webcomponent(
  "saml-federation-login",
  (element) => {
    useObservable(() => globalLanguageSubject, []);
    const [selectedIdP, setSelectedIdP] = useState(void 0);
    const { value: idpList } = useQuery(() => getIdPList()({ _id: 1, entityID: 1, displayName: 1 }), []);
    useEffect(() => {
      if (idpList && element.preselectedIdP) {
        const preselected = idpList.find((idp) => idp.entityID === element.preselectedIdP);
        if (preselected) {
          setSelectedIdP(preselected);
        }
      }
    }, [element.preselectedIdP, idpList]);
    const _openSelectionDialog = async () => {
      try {
        const result = await XDialog.showPopup({
          content: (resolve, reject, element2) => {
            let focused = false;
            if (!focused) {
              setTimeout(() => {
                const drop = element2.shadowRoot?.querySelector("x-dropdown-search-selection");
                drop?.focus();
                focused = true;
              }, 100);
            }
            return html`
              <x-card style="width: clamp(300px, 50vw, 600px); max-height: 80vh;">
                <x-flex direction="column" space="md">
                  <x-text variant="h4">${clientTranslator.translate("samlFederation", "selectInstitution")}</x-text>
                  <x-dropdown-search-selection
                    .placeholder=${clientTranslator.translate("samlFederation", "search")}
                    .search=${async (search) => {
              const searchResult = idpList?.filter(
                (idp) => idp.displayName?.toLowerCase().includes(search.toLowerCase())
              );
              return searchResult;
            }}
                    .renderString=${(idp) => idp?.displayName}
                    .guardKey=${"displayName"}
                    @value-picked=${(e) => resolve(e.detail.value)}
                  ></x-dropdown-search-selection>

                  <x-flex space="xs" justify="flex-end">
                    <x-button mode="outline" @click=${() => reject()}>
                      ${clientTranslator.translate("samlFederation", "cancel")}
                    </x-button>
                  </x-flex>
                </x-flex>
              </x-card>
            `;
          }
        });
        if (result) {
          setSelectedIdP(result);
        }
      } catch (error) {
        console.error("Dialog was closed or rejected", error);
      }
    };
    const _renderPopupButton = () => {
      return html` <x-button
        @click=${async () => {
        const loginRoute = getSamlLoginRoute({
          idpId: selectedIdP?._id,
          providerName: element.providerName,
          successRedirect: element.successRedirect
        });
        const url = A.absoluteUrl(loginRoute);
        const loginSuccessful = await popupAuthFlow(url);
        element.dispatchEvent(
          new CustomEvent("login-state-changed", {
            detail: { success: loginSuccessful === null, error: loginSuccessful },
            bubbles: true,
            composed: true
          })
        );
      }}
      >
        ${clientTranslator.translate("samlFederation", "login")} ${selectedIdP.displayName}
      </x-button>`;
    };
    const _renderRedirectButton = () => {
      return html` <x-button
        @click=${async () => {
        loginWithSAML({
          idpId: selectedIdP._id,
          providerName: element.providerName,
          successRedirect: element.successRedirect
        });
      }}
      >
        ${clientTranslator.translate("samlFederation", "login")} ${selectedIdP.displayName}
      </x-button>`;
    };
    return html`
      <x-flex space="xs">
        ${selectedIdP ? html`
              ${element.usePopup ? _renderPopupButton() : _renderRedirectButton()}

              <x-button mode="text" style="text-decoration: underline;" @click=${() => _openSelectionDialog()}>
                ${clientTranslator.translate("samlFederation", "selectAnother")}
              </x-button>
            ` : html` <x-button disabled>
                ${clientTranslator.translate("samlFederation", "buttonNoSelectedInstitution")}
              </x-button>
              <x-button mode="text" style="text-decoration: underline;" @click=${() => _openSelectionDialog()}>
                ${clientTranslator.translate("samlFederation", "selectInitial")}
              </x-button>`}
      </x-flex>
    `;
  },
  {
    properties: {
      preselectedIdP: { type: String },
      usePopup: { type: Boolean, attribute: "use-popup" },
      successRedirect: { type: String },
      providerName: { type: String }
    }
  }
);
export {
  SamlFederationLogin
};
